import type { IncomingUnconfirmedActionMessage } from "@engine/core";
import type { ClientStorageAdapter } from "@engine/network";
import type { RoundAggregate } from "@engine/round/aggregate";
import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from "react";
import type { SocketStatus } from "./ImperativeState";

export type HeaderConnectionData = {
  socketStatus: SocketStatus;
  /** See State.schemaSkew — build too old for the server's action schema. */
  schemaSkew: boolean;
  /** See State.sessionRejected — the server refused this session's socket. */
  sessionRejected: boolean;
  clientId: string;
  round: RoundAggregate;
  unconfirmedActions: Promise<
    { createdAt: number; message: IncomingUnconfirmedActionMessage }[]
  >;
  retryUnconfirmedActions: () => Promise<Error | null>;
  clearUnconfirmedActions: () => Promise<void>;
  reportError: () => void;
  storage: ClientStorageAdapter;
  connectedUserIds: string[];
};

type HeaderConnectionContextValue = {
  connectionData: HeaderConnectionData | null;
  setConnectionData: (data: HeaderConnectionData | null) => void;
};

const HeaderConnectionContext =
  createContext<HeaderConnectionContextValue | null>(null);

export function HeaderConnectionProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [connectionData, setConnectionData] =
    useState<HeaderConnectionData | null>(null);
  return (
    <HeaderConnectionContext.Provider
      value={{ connectionData, setConnectionData }}
    >
      {children}
    </HeaderConnectionContext.Provider>
  );
}

export function useHeaderConnection() {
  const ctx = useContext(HeaderConnectionContext);
  return ctx?.connectionData ?? null;
}

export function useSetHeaderConnection() {
  const ctx = useContext(HeaderConnectionContext);
  const setter = ctx?.setConnectionData;
  return useCallback(
    (data: HeaderConnectionData | null) => {
      setter?.(data);
    },
    [setter],
  );
}
